import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { EVIDENCE_CLASSIFICATION, FLAT_RATES, INFAKT_KIND_LABEL, INTERNAL_INVOICE_KIND, VAT_SYMBOLS, parseAmount, type InfaktLine, type InfaktTemplate } from '@biuro/shared';
import { api, type InfaktClient } from '../api.ts';
import { useData, useDeleteDoc, useMe, useSaveDoc, useToast } from '../hooks.tsx';
import { DeleteButton, Icon, Sheet } from '../ui.tsx';

type LineForm = Omit<InfaktLine, 'quantity' | 'price'> & { quantity: string; price: string };
type Form = Omit<InfaktTemplate, 'lines' | 'paymentDays'> & { lines: LineForm[]; paymentDays: string };

const emptyLine: LineForm = { name: '', unit: '', quantity: '1', price: '', priceMode: 'net', vat: '23', flat: '8.5', pkwiu: '', variable: 'none' };

export function TemplateForm({ id, onClose }: { id?: string; onClose: () => void }) {
  const { data } = useData();
  const me = useMe().data;
  const save = useSaveDoc();
  const del = useDeleteDoc();
  const toast = useToast();
  const t = id ? data.infaktTemplates[id] : undefined;
  const [q, setQ] = useState('');
  const [clients, setClients] = useState<InfaktClient[] | null>(null);
  const [searching, setSearching] = useState(false);
  const rates = useQuery({ queryKey: ['flatRates'], queryFn: api.infaktFlatRates, enabled: !!me?.infakt.enabled, staleTime: 3600_000, retry: false });

  const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm<Form>({
    defaultValues: {
      kind: t?.kind || 'invoice', name: t?.name || '', clientId: t?.clientId, clientName: t?.clientName || '', clientNip: t?.clientNip || '',
      classification: t?.classification || 'salary', transactionKind: t?.transactionKind || 'nonaccounting_sales',
      paymentMethod: t?.paymentMethod || 'transfer', paymentDays: String(t?.paymentDays ?? 14), notes: t?.notes || '', active: t?.active !== false,
      lines: (t?.lines || [emptyLine]).map((l) => ({ ...emptyLine, ...l, quantity: String(l.quantity ?? 1), price: l.price != null ? String(l.price).replace('.', ',') : '' })),
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'lines' });
  const kind = watch('kind');
  const evidence = kind === 'internal_evidence';
  const clientName = watch('clientName');

  const flatOptions = [...new Set([...(rates.data?.map((r) => r.symbol) || FLAT_RATES), ...fields.map((f) => f.flat)])];

  const search = async () => {
    setSearching(true);
    try {
      setClients(await api.infaktClients(q.trim()));
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setSearching(false);
    }
  };

  const submit = handleSubmit((v) => {
    const isEvidence = v.kind === 'internal_evidence';
    // Dowód wewnętrzny w inFakcie ma jedną pozycję, bez VAT, jednostki i PKWiU.
    const lines: InfaktLine[] = (isEvidence ? v.lines.slice(0, 1) : v.lines).map((l) => ({
      name: l.name.trim(), unit: isEvidence ? undefined : l.unit?.trim() || undefined, quantity: parseAmount(l.quantity) ?? 1, price: parseAmount(l.price) ?? 0,
      priceMode: isEvidence ? 'net' : l.priceMode, vat: isEvidence ? 'np' : l.vat, flat: l.flat.replace(',', '.'),
      pkwiu: isEvidence ? undefined : l.pkwiu?.trim() || undefined, variable: l.variable,
    }));
    if (v.kind === 'invoice' && !v.clientId) return toast('Wybierz klienta z inFaktu.');
    const doc: InfaktTemplate = {
      kind: v.kind, name: v.name.trim(), lines, notes: v.notes?.trim() || undefined, active: v.active,
      ...(v.kind === 'invoice'
        ? { clientId: Number(v.clientId), clientName: v.clientName, clientNip: v.clientNip, paymentMethod: v.paymentMethod || 'transfer', paymentDays: Number(v.paymentDays) || 0 }
        : {}),
      ...(isEvidence ? { classification: v.classification || 'salary' } : {}),
      ...(v.kind === 'internal_invoice' ? { transactionKind: v.transactionKind || 'nonaccounting_sales' } : {}),
    };
    save.mutate({ col: 'infaktTemplates', id: id || 'tpl-' + Date.now().toString(36), doc });
    toast('Zapisano szablon');
    onClose();
  });

  return (
    <Sheet title={t?.name || 'Nowy szablon'} onClose={onClose}
      footer={<>
        {id ? <DeleteButton onConfirm={() => { del.mutate({ col: 'infaktTemplates', id }); onClose(); }} /> : <span />}
        <div className="r"><button className="btn" onClick={onClose}>Anuluj</button><button className="btn pri" onClick={submit}>Zapisz</button></div>
      </>}>
      <form className="stack" onSubmit={submit}>
        <div className="fgrid">
          <div className="fld">
            <label htmlFor="t-kind">Rodzaj dokumentu</label>
            <select id="t-kind" {...register('kind')}>{Object.entries(INFAKT_KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          </div>
          <div className="fld">
            <label htmlFor="t-name">Nazwa szablonu</label>
            <input id="t-name" autoComplete="off" {...register('name', { required: true })} />
            {errors.name && <div className="hint err">Podaj nazwę.</div>}
          </div>
        </div>

        {kind === 'invoice' && (
          <div className="fgrid">
            <div className="fld full">
              <label htmlFor="t-q">Klient w inFakcie</label>
              {clientName && <div className="note ok" style={{ marginBottom: 8 }}>{clientName}{watch('clientNip') ? `, NIP ${watch('clientNip')}` : ''}</div>}
              <div className="copyrow">
                <input id="t-q" placeholder="Nazwa klienta" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), search())} />
                <button type="button" className="btn sm" disabled={!me?.infakt.enabled || searching} onClick={search}>{searching ? 'Szukam…' : 'Szukaj'}</button>
              </div>
              {!me?.infakt.enabled && <div className="hint">Najpierw dodaj klucz API inFaktu na serwerze (INFAKT_API_KEY).</div>}
              {clients && (
                <div className="pick">
                  {clients.length ? clients.map((c) => (
                    <button type="button" key={c.id} className="btn sm" onClick={() => {
                      setValue('clientId', c.id); setValue('clientName', c.name); setValue('clientNip', c.nip); setClients(null);
                    }}>{c.name}{c.nip ? `, NIP ${c.nip}` : ''}</button>
                  )) : <span className="hint">Nie znalazłem klienta. Dodaj go najpierw w inFakcie.</span>}
                </div>
              )}
            </div>
            <div className="fld"><label htmlFor="t-days">Termin płatności (dni)</label><input id="t-days" inputMode="numeric" {...register('paymentDays')} /></div>
            <div className="fld">
              <label htmlFor="t-pay">Forma płatności</label>
              <select id="t-pay" {...register('paymentMethod')}><option value="transfer">Przelew</option><option value="cash">Gotówka</option><option value="card">Karta</option></select>
            </div>
          </div>
        )}

        {kind === 'internal_invoice' && (
          <div className="fgrid">
            <div className="fld">
              <label htmlFor="t-trans">Rodzaj transakcji w inFakcie</label>
              <select id="t-trans" {...register('transactionKind')}>
                {Object.entries(INTERNAL_INVOICE_KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
        )}

        {evidence && (
          <div className="fgrid">
            <div className="fld">
              <label htmlFor="t-class">Klasyfikacja w inFakcie</label>
              <select id="t-class" {...register('classification')}>
                {Object.entries(EVIDENCE_CLASSIFICATION).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
        )}

        <div>
          <div className="lbl">{evidence ? 'Pozycja' : 'Pozycje'}</div>
          {evidence && fields.length > 1 && <div className="note warn" style={{ marginBottom: 8 }}>Dowód wewnętrzny w inFakcie ma jedną pozycję. Zapiszę tylko pierwszą.</div>}
          <div className="stack">
            {fields.map((f, i) => (
              <div key={f.id} className="linebox">
                <div className="fgrid">
                  <div className="fld full"><label htmlFor={`l${i}-name`}>Nazwa pozycji</label><input id={`l${i}-name`} autoComplete="off" {...register(`lines.${i}.name`, { required: true })} /></div>
                  <div className="fld">
                    <label htmlFor={`l${i}-var`}>Co wpisujesz co miesiąc</label>
                    <select id={`l${i}-var`} {...register(`lines.${i}.variable`)}>
                      <option value="none">Nic – stała pozycja</option>
                      <option value="quantity">Ilość (np. godziny)</option>
                      <option value="amount">Kwotę</option>
                    </select>
                  </div>
                  {!evidence && (
                    <div className="fld">
                      <label htmlFor={`l${i}-mode`}>Kwoty</label>
                      <select id={`l${i}-mode`} {...register(`lines.${i}.priceMode`)}><option value="net">netto</option><option value="gross">brutto</option></select>
                    </div>
                  )}
                  <div className="fld"><label htmlFor={`l${i}-price`}>Cena jednostkowa (zł)</label><input id={`l${i}-price`} inputMode="decimal" autoComplete="off" {...register(`lines.${i}.price`)} /></div>
                  <div className="fld"><label htmlFor={`l${i}-qty`}>Ilość domyślna</label><input id={`l${i}-qty`} inputMode="decimal" autoComplete="off" {...register(`lines.${i}.quantity`)} /></div>
                  {!evidence && <>
                    <div className="fld"><label htmlFor={`l${i}-unit`}>Jednostka</label><input id={`l${i}-unit`} placeholder="godz." autoComplete="off" {...register(`lines.${i}.unit`)} /></div>
                    <div className="fld">
                      <label htmlFor={`l${i}-vat`}>Stawka VAT</label>
                      <select id={`l${i}-vat`} {...register(`lines.${i}.vat`)}>{VAT_SYMBOLS.map((s) => <option key={s} value={s}>{/^\d/.test(s) ? `${s}%` : s}</option>)}</select>
                    </div>
                  </>}
                  <div className="fld">
                    <label htmlFor={`l${i}-flat`}>Stawka ryczałtu</label>
                    <select id={`l${i}-flat`} {...register(`lines.${i}.flat`)}>{flatOptions.map((s) => <option key={s} value={s}>{s.replace('.', ',')}%</option>)}</select>
                  </div>
                  {!evidence && <div className="fld"><label htmlFor={`l${i}-pkwiu`}>PKWiU (opcjonalnie)</label><input id={`l${i}-pkwiu`} autoComplete="off" {...register(`lines.${i}.pkwiu`)} /></div>}
                </div>
                {fields.length > 1 && <button type="button" className="btn sm danger" onClick={() => remove(i)}>Usuń pozycję</button>}
              </div>
            ))}
          </div>
          {!evidence && <button type="button" className="btn sm" style={{ marginTop: 10 }} onClick={() => append({ ...emptyLine })}><Icon.plus />Dodaj pozycję</button>}
        </div>

        <div className="fld"><label htmlFor="t-notes">Uwagi na dokumencie</label><textarea id="t-notes" {...register('notes')} /></div>
        <label className="chk"><input type="checkbox" {...register('active')} /> Aktywny – wystawiam co miesiąc</label>
      </form>
    </Sheet>
  );
}
